import { useEffect, useState } from "react";
import { ArrowLeftRight, Check, X, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { DEMO_TRANSFERS } from "./mdtDemo";
import { inputCls, btnCls, toastOk } from "./RosterTab";

/* Transfer requests — officers apply to another department; supervisors approve/deny.
   Approval moves the roster entry and resets them to the target dept's entry rank. */

const STATUS = { pending: "text-amber-300 border-amber-500/40", approved: "text-emerald-300 border-emerald-500/40", denied: "text-red-300 border-red-500/40" };

export default function TransfersTab({ G }) {
  const { serverId, token, demo, sfx, me, config, refreshMe } = G;
  const [transfers, setTransfers] = useState(null);
  const [toDept, setToDept] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => {
    if (demo) { setTransfers(DEMO_TRANSFERS); return; }
    api.get(`/nc/servers/${serverId}/cad/dept/transfers`, { params: { token } })
      .then((r) => setTransfers(r.data.transfers)).catch(() => setTransfers([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!toDept || reason.trim().length < 5) return;
    sfx.keyTick();
    if (demo) {
      setTransfers((t) => [{ id: `l${Date.now()}`, name: me.name, playercode: me.playercode, from_dept: me.dept?.name || "Unassigned", to_dept: config.departments.find((d) => d.id === toDept)?.name, reason: reason.trim(), status: "pending", at: new Date().toISOString() }, ...t]);
      setReason(""); setToDept(""); return;
    }
    setBusy("new");
    try {
      const r = await api.post(`/nc/servers/${serverId}/cad/dept/transfers`, { to_dept_id: toDept, reason: reason.trim() }, { params: { token } });
      toastOk(r.data.message); setReason(""); setToDept(""); load();
    } catch (e) { import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Failed")); }
    finally { setBusy(""); }
  };
  const decide = async (t, approve) => {
    sfx.keyTick();
    if (demo) { setTransfers((ts) => ts.map((x) => x.id === t.id ? { ...x, status: approve ? "approved" : "denied", decided_by: me.name } : x)); return; }
    setBusy(t.id);
    try {
      const r = await api.post(`/nc/servers/${serverId}/cad/dept/transfers/${t.id}/decide`, { approve }, { params: { token } });
      toastOk(r.data.message); load(); refreshMe();
    } catch (e) { import("sonner").then(({ toast }) => toast.error(e.response?.data?.detail || "Failed")); }
    finally { setBusy(""); }
  };

  if (!transfers) return <div className="flex justify-center p-8"><Loader2 className="w-4 h-4 animate-spin text-sky-400" /></div>;

  return (
    <div className="p-3.5 flex flex-col gap-2" data-testid="transfers-tab">
      {/* request form */}
      <div className="border border-white/10 bg-black/40 p-3 flex flex-col gap-2">
        <p className="font-mono text-[8px] uppercase tracking-[0.25em] text-zinc-500 flex items-center gap-1.5"><ArrowLeftRight className="w-3 h-3 text-sky-400" /> Request department transfer</p>
        <div className="flex gap-1.5">
          <select data-testid="transfer-dept" value={toDept} onChange={(e) => setToDept(e.target.value)} className={`${inputCls} flex-1`}>
            <option value="">Target department…</option>
            {config.departments.filter((d) => d.id !== me.dept?.id).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
          </select>
          <button data-testid="transfer-submit" onClick={submit} disabled={busy === "new" || !toDept || reason.trim().length < 5}
            className={`${btnCls} border-sky-500/50 text-sky-300 hover:bg-sky-500/10`}>{busy === "new" ? "…" : "Submit"}</button>
        </div>
        <textarea data-testid="transfer-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Reason for the transfer…" className={`${inputCls} w-full resize-none`} />
      </div>
      {transfers.map((t) => (
        <div key={t.id} data-testid={`transfer-${t.id.slice(0, 6)}`} className="border border-white/10 bg-black/40 p-3 flex flex-col gap-1.5">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-mono text-[10px] font-bold text-zinc-100">{t.name} <span className="text-zinc-500">#{t.playercode}</span></p>
            <span className="font-mono text-[8px] text-zinc-400 uppercase">{t.from_dept} → <span className="text-sky-300">{t.to_dept}</span></span>
            <span className={`ml-auto font-mono text-[8px] uppercase border px-1.5 py-0.5 ${STATUS[t.status]}`}>{t.status}</span>
          </div>
          <p className="font-mono text-[9px] text-zinc-400 leading-snug">{t.reason}</p>
          <p className="font-mono text-[7.5px] text-zinc-600 uppercase">{String(t.at).slice(0, 10)}{t.decided_by ? ` · decided by ${t.decided_by}` : ""}</p>
          {me.is_supervisor && t.status === "pending" && (
            <div className="flex gap-1.5 mt-1">
              <button data-testid={`transfer-approve-${t.id.slice(0, 6)}`} disabled={busy === t.id} onClick={() => decide(t, true)} className={`${btnCls} border-emerald-500/50 text-emerald-300 hover:bg-emerald-500/10 flex items-center gap-1`}><Check className="w-3 h-3" /> Approve</button>
              <button data-testid={`transfer-deny-${t.id.slice(0, 6)}`} disabled={busy === t.id} onClick={() => decide(t, false)} className={`${btnCls} border-red-500/50 text-red-300 hover:bg-red-500/10 flex items-center gap-1`}><X className="w-3 h-3" /> Deny</button>
            </div>
          )}
        </div>
      ))}
      {transfers.length === 0 && <p className="font-mono text-[9px] text-zinc-500 uppercase tracking-[0.2em] p-3 text-center">No transfer requests on file</p>}
    </div>
  );
}
